/**
 * `channel-picker` — the assistant's reusable "connect a sales channel" artifact.
 *
 * Attached to an assistant message as a block (see
 * `domains/aiAssistant/state/messageBlocks.ts`). Onboarding seeds it on the
 * welcome turn, but nothing about it is onboarding-specific: any future turn
 * that needs the user to connect a channel emits the same block type and gets
 * the same UI, the same catalog and the same behaviour.
 *
 * ─── It CONNECTS. It does not just point at Integrations ────────────────────
 * For an `oauth` connector the whole flow runs from here: the block asks the
 * server for the authorization URL and opens the provider's consent window.
 * That covers the marketplaces and the ecommerce platforms — the overwhelming
 * majority of what a user connects first. Connectors whose setup needs a store
 * URL get one inline field, because a store URL is public information, not a
 * credential.
 *
 * Two kinds of connector are deliberately handed off to Integrations instead:
 *
 *   • `api-key` — connecting means typing a secret. The conversation is
 *     PERSISTED server-side as an aggregate, so an API key typed into a chat
 *     block would be written into a transcript. Credentials belong in the
 *     Integrations form, which is built for them.
 *   • `direct-provision` — needs a warehouse / price-list association, which is
 *     a real form and not a two-field artifact.
 *
 * Either way the user is never stuck: the secondary action opens
 * `/integrations/channels?connect=<connectorId>` with that connector already
 * selected, and it is offered for EVERY connector — "conectá acá, o llevame
 * allá" — which is the escape hatch this flow was asked to keep.
 *
 * ─── Where the catalog comes from ───────────────────────────────────────────
 * `integrations/shared` — the same connector readmodel the Integrations page
 * reads, filtered to `salesChannel`. No second source of truth, and connectors
 * added backend-side appear here with no change.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Loader, TextInput } from '@mantine/core';
import { IconCheck, IconExternalLink } from '@tabler/icons-react';
import {
  useSharedIntegrationsSelector,
  selectors as integrationSelectors
} from '@domains/integrations/shared/state/store';
import { resolveSetupPanelKind } from '@domains/integrations/shared/state/capabilityGates';
import type { ConnectorEntry } from '@domains/integrations/shared/state/state';
import type { MessageBlockRendererProps } from '@domains/aiAssistant/state/messageBlocks';
import { useAIAssistantActions, useAIAssistantSelector } from '@domains/aiAssistant';
import useBackend, { BackendContextType } from '@contexts/useBackend/useBackend';
import { componentsTestIds } from '@components/testids';
import classes from './ChannelPickerBlock.module.css';

/**
 * Payload the emitter controls. Every field optional: a bare
 * `{ type: 'channel-picker', payload: {} }` is a valid, useful block.
 */
export type ChannelPickerPayload = {
  /** Restrict the list to these connector ids. Omit to offer the whole catalog. */
  only?: string[];
  /** Connector ids to leave out — e.g. ones already connected. */
  exclude?: string[];
  /** Overrides the default call to action. */
  ctaLabel?: string;
};

const initials = ( name: string ): string =>
  name.replace( /[^A-Za-zÀ-ÿ0-9 ]/g, '' ).split( /\s+/ )
.filter( Boolean )
    .slice( 0, 2 )
.map(( w ) => w[0])
.join( '' )
.toUpperCase() || '?';

export const ChannelPickerBlock = ({ block }: MessageBlockRendererProps ) => {
  const { t, i18n } = useTranslation( 'interactions' );
  const { backend } = useBackend() as BackendContextType;
  const navigate = useNavigate();

  // `label` is a multilang map ({ es, en, pt }), not a string. Same fallback as
  // ConnectorList: current language -> es -> the connector id.
  const lang = i18n.language?.split( '-' )[0] || 'es';
  const labelOf = ( c: { label?: Record<string, string>; id: string }): string =>
    c.label?.[lang] || c.label?.es || c.id;

  const payload = ( block.payload ?? {}) as ChannelPickerPayload;

  const [ selected, setSelected ] = useState<string | null>( null );
  const [ storeUrl, setStoreUrl ] = useState( '' );
  const [ busy, setBusy ] = useState( false );
  const [ resolved, setResolved ] =
    useState<{ channel: string; kind: 'authorizing' | 'handedOff' } | null>( null );

  // Two columns only when there is room for them. The floating window is too
  // narrow, so the same block reflows to a single column there.
  const isFullscreen = useAIAssistantSelector(( s ) => s.panelMode === 'fullscreen' );
  const { setPanelMode } = useAIAssistantActions();

  const connectorsLoading = useSharedIntegrationsSelector(
    integrationSelectors.connectorsLoading
  );
  const salesChannels = useSharedIntegrationsSelector(
    ( s ) => integrationSelectors.selectConnectorsByKind( s, 'salesChannel' )
  );

  const options = useMemo(() => {
    const only = payload.only;
    const exclude = new Set( payload.exclude ?? []);
    return salesChannels
      .filter(( c ) => ( only ? only.includes( c.id ) : true ))
      .filter(( c ) => !exclude.has( c.id ))
      .sort(( a, b ) => labelOf( a ).localeCompare( labelOf( b )));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ salesChannels, payload.only, payload.exclude, lang ]);

  const chosen = options.find(( c ) => c.id === selected );
  const panelKind = resolveSetupPanelKind( chosen );
  const needsStoreUrl = panelKind === 'oauth' && Boolean( chosen?.settings?.needsUrl );
  /** True when this connector can be finished from inside the conversation. */
  const connectsInline = panelKind === 'oauth';

  /**
   * What happens once you pick this one — the same expectation the reference
   * design set with its ETA column, except derived from the connector's declared
   * capabilities instead of invented. There is no duration anywhere in the
   * readmodel, and a made-up "~4 min" is worse than no number at all.
   */
  const subtitleFor = ( connector: ConnectorEntry ): string => {
    switch ( resolveSetupPanelKind( connector )) {
      case 'oauth':
        return t( 'ai_channel_picker_kind_oauth', 'Autorizás con tu cuenta' );
      case 'apiKey':
        return t( 'ai_channel_picker_kind_apikey', 'Necesita credenciales' );
      case 'directProvision':
        return t( 'ai_channel_picker_kind_direct', 'Se crea al instante' );
      default:
        return t( 'ai_channel_picker_kind_managed', 'Lo configura Teselly' );
    }
  };

  /** Hand the choice to Integrations, which owns every setup this block does not. */
  const openInIntegrations = ( connector: ConnectorEntry ) => {
    setResolved({ channel: labelOf( connector ), kind: 'handedOff' });
    // Fullscreen covers the app, so a navigation would land behind the panel.
    setPanelMode( 'floating' );
    navigate( `/integrations/channels?connect=${ encodeURIComponent( connector.id ) }` );
  };

  const connectHere = async ( connector: ConnectorEntry ) => {
    setBusy( true );
    try {
      const cleaned = storeUrl.trim().replace( /^https?:\/\//, '' );
      const params: { connectorId: string; route: string; site?: string; storeUrl?: string } =
        needsStoreUrl
          ? { connectorId: connector.id, route: 'get-authorization-url', storeUrl: `https://${cleaned}` }
          : { connectorId: connector.id, route: 'get-authorization-url', site: 'MLA' };

      // The generated client type for this endpoint is stale — it declares only
      // `{ connectorId, route }` params and a `void` return, but the endpoint
      // accepts `site` / `storeUrl` and returns `{ url }`. Same assertion the
      // Integrations modal makes; both call the one endpoint.
      const authResult = ( await backend.get.systems_integrations_webhooks_get(
        params
      )) as unknown as { url?: string };

      if ( authResult?.url ) {
        window.open( authResult.url, '_blank', 'noopener,noreferrer' );
      }
      setResolved({ channel: labelOf( connector ), kind: 'authorizing' });
    } finally {
      // A thrown error leaves the block live so the user can retry — the global
      // error handler surfaces the reason.
      setBusy( false );
    }
  };

  // Once acted on, the block collapses to a statement of what happened. A picker
  // that stays live in the transcript invites a second click, and the message
  // above it is already history.
  if ( resolved ) {
    return (
      <div className={classes.block}>
        <div className={classes.resolved}>
          <IconCheck size={14} stroke={2.4} />
          <span>
            {resolved.kind === 'authorizing'
              ? t(
                'ai_channel_picker_resolved_authorizing',
                'Abrí la autorización de {{channel}} en otra pestaña. Cuando la completes, el canal queda conectado.',
                { channel: resolved.channel }
              )
              : t(
                'ai_channel_picker_resolved_handed_off',
                'Te llevé a Integraciones para terminar de conectar {{channel}}.',
                { channel: resolved.channel }
              )}
          </span>
        </div>
      </div>
    );
  }

  if ( !connectorsLoading && options.length === 0 ) {
    return (
      <div className={classes.block}>
        <div className={classes.empty}>
          {t( 'ai_channel_picker_empty', 'No hay canales disponibles para conectar.' )}
        </div>
      </div>
    );
  }

  const canSubmit = Boolean( chosen ) && ( !needsStoreUrl || storeUrl.trim().length > 0 );

  return (
    <div className={classes.block} data-testid={componentsTestIds.aiChannelPicker}>
      <div className={classes.head}>
        <span>{t( 'ai_channel_picker_title', 'Canales disponibles' )}</span>
        <span className={classes.headCount}>
          {connectorsLoading
            ? t( 'ai_channel_picker_loading', 'Cargando…' )
            : t( 'ai_channel_picker_count', '{{count}} integraciones', { count: options.length })}
        </span>
      </div>

      <div className={classes.rows} data-cols={isFullscreen ? 2 : 1}>
        {options.map(( connector ) => {
          const name = labelOf( connector );
          const isOn = selected === connector.id;
          return (
            <button
              key={connector.id}
              type="button"
              className={classes.row}
              aria-selected={isOn}
              data-testid={componentsTestIds.aiChannelPickerOption( connector.id )}
              onClick={() => {
                setSelected( connector.id );
                setStoreUrl( '' );
              }}
            >
              <span className={classes.mark}>
                {connector.imageUrl
                  ? <img src={connector.imageUrl} alt="" />
                  : initials( name )}
              </span>
              <span className={classes.rowText}>
                <span className={classes.name}>{name}</span>
                <span className={classes.meta}>{subtitleFor( connector )}</span>
              </span>
              <span className={classes.check}><IconCheck size={15} stroke={2.4} /></span>
            </button>
          );
        })}
      </div>

      {needsStoreUrl && (
        <div className={classes.urlRow}>
          <TextInput
            size="xs"
            radius="xs"
            value={storeUrl}
            onChange={( event ) => setStoreUrl( event.currentTarget.value )}
            placeholder={t( 'ai_channel_picker_store_url_placeholder', 'mitienda.com' )}
            label={t( 'ai_channel_picker_store_url_label', 'Dirección de tu tienda' )}
            data-testid={componentsTestIds.aiChannelPickerStoreUrl}
          />
        </div>
      )}

      <div className={classes.foot}>
        <button
          type="button"
          className={classes.primaryBtn}
          disabled={!canSubmit || busy}
          data-testid={componentsTestIds.aiChannelPickerConnect}
          onClick={() => {
            if ( !chosen ) return;
            if ( connectsInline ) void connectHere( chosen );
            else openInIntegrations( chosen );
          }}
        >
          {busy && <Loader size={12} color="white" />}
          {payload.ctaLabel
            ?? ( chosen
              ? t( 'ai_channel_picker_connect_named', 'Conectar {{channel}}', { channel: labelOf( chosen ) })
              : t( 'ai_channel_picker_connect', 'Conectar' ))}
        </button>

        {/* Offered for EVERY connector, including the ones this block can finish
            itself: "conectá acá, o llevame allá". */}
        <button
          type="button"
          className={classes.subtleBtn}
          disabled={!chosen || busy}
          data-testid={componentsTestIds.aiChannelPickerOpenIntegrations}
          onClick={() => chosen && openInIntegrations( chosen )}
        >
          <IconExternalLink size={13} />
          {t( 'ai_channel_picker_open_integrations', 'Abrir en Integraciones' )}
        </button>

        <span className={classes.footNote}>
          {t( 'ai_channel_picker_hint', 'Podés sumar más después' )}
        </span>
      </div>
    </div>
  );
};

export default ChannelPickerBlock;
